import { faBriefcaseClock } from "@fortawesome/free-solid-svg-icons";
import {
    ChordContainer,
    ContainerAtom,
    HorizontalNoteContainer,
} from "../model/containers";
import {
    Chord,
    Note,
    NoteDuration,
    NoteVelocity,
    Tone
} from "../model/core";
import {
    IntervalPattern,
    ScalePattern,
    Tonality
} from "../model/scales";
import {
    listVirtualInstruments,
    RydExternalMidiInstrument,
    RydSFInstrument
} from "../model/instrument";
import {
    getDefaulAudioSource,
    MidiSink,
    MidiSource
} from "../audio/routing";
import { RydMixer } from "../audio/mixer";
import {
    ProjectMixerState,
    ProjectUIState,
    RydProject
} from "../model/project";
import { ComputedContextFrame } from "../model/context";
import { RydTrackUIState } from "../model/tracks";
import { RydMidiInputSource } from "../audio/input";


type SaveFileNoteEvent = {
    step: number,
    payload: { index: number },
    duration: { value: number },
    velocity: number
};

type SaveFileChordEvent = {
    step: number,
    payload: { root: { index: number }, family: string, tones: { index: number }[] },
    duration: { value: number },
    velocity: number
};

function readChord(chord: any): Chord {
    // Convert the chord from the serialized format to a Chord object
    const tones = chord.tones.map(t => Tone.fromIndex(t.index));
    const c = new Chord(Tone.fromIndex(chord.root.index), chord.family);
    c.tones = tones;
    return c;
}

function readChordContainer(container: any): ChordContainer {
    const cc = new ChordContainer({
        length: container.length,
        ppq: container.ppq
    });
    container.grid.forEach((chord, i) => {
        if (!chord) return;
        // VOMIT
        cc.put(chord.step, {
            step: chord.step,
            payload: readChord(chord.payload),
            duration: new NoteDuration(chord.duration.value),
            velocity: 100
        });
    });

    return cc;
}

function readNoteContainer(container: any): HorizontalNoteContainer {
    const hnc = new HorizontalNoteContainer({
        length: container.length,
        ppq: container.ppq
    });
    container.grid.forEach((item: SaveFileNoteEvent) => {
        hnc.put(item.step, {
            step: item.step,
            payload: Note.fromIndex(item.payload.index),
            duration: new NoteDuration(item.duration.value),
            velocity: item.velocity as NoteVelocity
        });
    });
    return hnc;
}

export class ContainerSerializer {
    static loadInputLineSaveFile(
        input: any,
        audioContext: AudioContext
    ): RydProject {

        if (!audioContext) {
            throw new Error('AudioContext is not initialized. Please initialize it before loading a project.');
        }

        console.time('loadProject');

        const il = RydProject.createForNewProject(input.name || 'Loaded Project from file');
        il.contextFrame.computed = new ComputedContextFrame();
        il.contextFrame.computed.timeSignature = input.contextFrame.timeSignature;
        il.contextFrame.computed.metronome = input.contextFrame.metronome;

        if (input.contextFrame.tonality) {
            il.contextFrame.computed.tonality = new Tonality(
                Tone.fromIndex(input.contextFrame.tonality.center.index),
                new ScalePattern(new IntervalPattern(
                    input.contextFrame.tonality.intervalPattern.name,
                    input.contextFrame.tonality.intervalPattern.intervals
                )));
        }

        let ppq = 1;
        input.lines.forEach((container, i) => {
            switch (container.type) {
                case 'chordGroup':
                    il.put(i, readChordContainer(container));
                    break;
                case 'noteGroup':
                    il.put(i, readNoteContainer(container), { skipLengthCheck: true });
                    ppq = container.ppq > ppq ? container.ppq : ppq;
                    break;
                default:
                    throw Error(`Unknown container type ${container.type}`);
            }
        });

        input.instrumentAssignments.forEach((assignment) => {
            const [track, instrumentDef] = assignment;
            // console.log(`Loading instrument assignment for track ${track} with instrument`, instrumentDef);
            switch (instrumentDef.outputType) {
                case 'VSYNTH':
                    // FIXME
                    const vinst = listVirtualInstruments().find((v) => v.id === instrumentDef.id);
                    if (!vinst) {
                        throw new Error(`Virtual instrument with id ${instrumentDef.id} not found.`);
                    }
                    il.instrumentAssignments.set(track, vinst.create(audioContext));

                    break;
                case 'SF2':
                    const sf = new RydSFInstrument(
                        instrumentDef.id,
                        instrumentDef.name,
                        instrumentDef.id,
                        audioContext,
                    );
                    il.instrumentAssignments.set(track, sf);
                    break;
                case 'EXTMIDI':
                    const ext = new RydExternalMidiInstrument(
                        instrumentDef.id,
                        instrumentDef.name,
                        new MidiSink(
                            instrumentDef.name,
                            instrumentDef.id,
                            instrumentDef.outputType
                        )
                    );

                    il.instrumentAssignments.set(track, ext);
                    break;
                default:
                    throw Error(`Unknown output type ${instrumentDef.outputType} for instrument assignment`);
            }
        });

        const defaultInput = getDefaulAudioSource();

        input.sourceAssignments.forEach((assignment) => {
            const [track, source] = assignment;
            console.log(`Loading source assignment for track ${track} with source`, source);
            il.sourceAssignments.set(
                track,
                source.id === defaultInput.id
                    ? defaultInput
                    : new RydMidiInputSource(source.name, source.id, new MidiSource(source.name, source.id, source.type)));
        });

        il.bookmarks = input.bookmarks ? input.bookmarks : [];

        console.log('Loaded project', input);
        il.uiState = deserializeUiState(input.uiState || {
            tracks: [],
            zoomFactor: ppq
        });

        console.log('Loaded Mixer State', input.mixerState);

        il.mixerState = deserializeMixerState(input.mixerState || {
            master: {
                gain: 1.0,
            },
            tracks: []
        });

        console.timeEnd('loadProject');
        return il;
    }

    static serializeProject(line: RydProject): string {
        console.log('Serializing project, mixer', line.mixerState, line);
        console.time('serializeProject');
        const raw = {
            name: line.name,
            contextFrame: {
                timeSignature: line.contextFrame.computed.timeSignature,
                metronome:     line.contextFrame.computed.metronome,
                tonality:      line.contextFrame.computed.tonality ? {
                    center: { index: line.contextFrame.computed.tonality.center.index },
                    intervalPattern: {
                        name: line.contextFrame.computed.tonality.scalePattern.pattern.name,
                        intervals: line.contextFrame.computed.tonality.scalePattern.pattern.intervals
                    }
                } : undefined
            } as ComputedContextFrame,
            lines: line.getAll().map((container, i) => {
                if (container instanceof ChordContainer) {
                    return {
                        type: 'chordGroup',
                        length: (container as ChordContainer).getLength(),
                        ppq: (container as ChordContainer).getPPQ(),
                        grid: convertChordGrid((container as ChordContainer).getGrid())
                    };
                } else if (container instanceof HorizontalNoteContainer) {
                    return {
                        type: 'noteGroup',
                        length: (container as HorizontalNoteContainer).getLength(),
                        ppq: (container as HorizontalNoteContainer).getPPQ(),
                        grid: convertNoteGrid((container as HorizontalNoteContainer).getGrid())
                    };
                } else {
                    console.log('Unknown container type for serialization', container);
                    throw Error(`Unknown container type for serialization ${container}`);
                }
            }),
            uiState: serializeUIState(line.uiState),
            mixerState: serializeMixerState(line.mixerState),
            instrumentAssignments: Array.from(line.instrumentAssignments.entries()).map(([key, value]) => {
                return [key, {
                    outputType: value.getOutputType(),
                    name: value.name,
                    id: value.id,
                }];
            }),
            sourceAssignments: Array.from(line.sourceAssignments.entries()).map(([key, value]) => {
                return [key, {
                    sourceType: value.type,
                    name: value.getdisplayName(),
                    id: value.id,
                }];
            }),
            // FIXME: add missing track-level bookmarks
            bookmarks: line.bookmarks
        };

        const lineJSON = JSON.stringify(raw);
        console.timeEnd('serializeProject');
        return lineJSON;
    }
}

function convertChordGrid(grid: ContainerAtom<Chord>[][]): SaveFileChordEvent[] {
    const r: SaveFileChordEvent[] = [];
    grid.forEach((row, i) => {
        if (!row) return;
        row.map((atom) => {
            if (!atom) return null;
            return {
                step: i,
                payload: {
                    root: { index: atom.payload.root.index },
                    family: atom.payload.family,
                    tones: atom.payload.tones.map(t => ({ index: t.index }))
                },
                duration: { value: atom.duration.value },
                velocity: atom.velocity
            } as SaveFileChordEvent;
        }).forEach((chord, j) => {
            if (!chord) return;
            r.push(chord);
        });
    })
    return r;
}

function convertNoteGrid(grid: ContainerAtom<Note>[][]): SaveFileNoteEvent[] {
    const r: SaveFileNoteEvent[] = [];
    grid.forEach((row, i) => {
        if (!row) return;
        row.map((atom) => {
            if (!atom) return null;
            return {
                step: i,
                payload: { index: atom.payload.index },
                duration: { value: atom.duration.value },
                velocity: atom.velocity
            } as SaveFileNoteEvent;
        }).forEach((note, j) => {
            if (!note) return;
            r.push(note);
        });
    })
    return r;
}

function serializeMixerState(mixerState: ProjectMixerState) {
    const state = {
        master: {
            gain: mixerState.master.gain,
        },
        tracks: Array.from(mixerState.tracks.values()).map(track => ({
            id: track.id,
            gain: track.gain,
            pan: track.pan,
            muted: track.muted,
            solo: track.solo,
        }))
    };
    return state;
}

function deserializeMixerState(input: any): ProjectMixerState {
    const state: ProjectMixerState = {
        master: {
            gain: input.master.gain || 1.0,
        },
        tracks: new Map<number, RydMixer.TrackParams>()
    };
    (input.tracks || []).forEach((track: any) => {
        state.tracks.set(track.id, {
            id: track.id,
            gain: track.gain || 1.0,
            pan: track.pan || 0.0,
            muted: track.muted || false,
            solo: track.solo || false,
        } as RydMixer.TrackParams);
    });
    return state;
}

function serializeUIState(uiState: ProjectUIState) {
    const state = {
        tracks: Array.from(uiState.tracks.values()).map(track => ({
            trackId: track.trackId,
            collapsed: track.collapsed,
            noteRange: track.noteRange,
            tonalityVisible: track.tonalityVisible,
            color: track.color,
        })),
        zoomFactor: uiState.zoomFactor,
        gridResolution: uiState.gridResolution,
        snapToGrid: uiState.snapToGrid,
        newNoteDuration: uiState.newNoteDuration,
        editMode: uiState.editMode,
        globalKeybindingsSuspended: uiState.globalKeybindingsSuspended,
    };
    return state;
}

function deserializeUiState(input: any): ProjectUIState {
    const state: ProjectUIState = {
        tracks: new Map<number, RydTrackUIState>(),
        zoomFactor: input.zoomFactor || 1,
        gridResolution: input.gridResolution || 1,
        snapToGrid: input.snapToGrid || false,
        newNoteDuration: input.newNoteDuration || 1,
        // FIXME: these should not have defaults here
        editMode: input.editMode || 'select',
        globalKeybindingsSuspended: input.globalKeybindingsSuspended || false,
    };

    (input.tracks || []).forEach((track: any) => {
        state.tracks.set(track.trackId, {
            trackId: track.trackId,
            collapsed: track.collapsed || false,
            noteRange: track.noteRange || { min: 0, max: 107 },
            tonalityVisible: track.tonalityVisible || false,
            color: track.color || '#ffffff',
        } as RydTrackUIState);
    });
    return state;
}